import { Component, ChangeDetectionStrategy, ViewChild, ViewContainerRef, inject } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { partnerIdForOrg } from '@crewdle/white-label';
import { WhiteLabelService } from '@crewdle/white-label/angular';
import { LocalFileBrokerService } from './services/local-files/local-file-broker.service';
import { LocalFilesService } from './services/local-files/local-files.service';
import { environment } from '../environments/environment';

// Services
import { defaultProductHome } from './config/product';
import { AnalyticEventService } from './services/analytic-event/analytic-event.service';
import { AppHeaderService } from './services/app-header/app-header.service';
import { FirebaseService } from './services/firebase/firebase.service';
import { ForceUpdateService } from './services/force-update/force-update.service';
import { IntervalService } from './services/interval/interval.service';
import { ShellWebUpdateService } from './services/shell-web-update/shell-web-update.service';
import { firstRouteRendered } from './utils/first-route-rendered';
import { LanguageChangeService } from 'src/app/services/language-change/language-change.service';
import { LocalStorageService } from './services/local-storage/local-storage.service';
import { LoggingChannels, LoggingService } from './services/logging/logging.service';
import { MessagingListenerService } from './services/messaging-listener/messaging-listener.service';
import { ModalService } from './services/modal/modal.service';
import { MuteService } from './services/mute/mute.service';
import { NativeStatusService } from './services/native-status/native-status.service';
import { NotificationCenterService } from './services/notification-center/notification-center.service';
import { NotificationService } from './services/notification/notification.service';
import { NativeNavigationService } from './services/native-navigation/native-navigation.service';
import { PushRegistrationService } from './services/push-registration/push-registration.service';
import { AgentService } from './services/agent/agent.service';
import { OrganizationService } from './services/organization/organization.service';
import { ProductAccessService } from './services/product-access/product-access.service';
import { TabNotificationService } from './services/tab-notification/tab-notification.service';
import { ThemeService } from './services/theme/theme.service';
import { SidebarNavService } from './services/sidebar-nav/sidebar-nav.service';
import { SupportChatbotService } from './components/support-chatbot/support-chatbot.service';
import { UserService } from './services/user/user.service';
import { AppVersionService } from './services/app-version/app-version.service';
import { PlatformService } from './services/platform/platform.service';
import { RedirectService } from './services/redirect/redirect.service';
import { NavigationProgressService } from './services/navigation-progress/navigation-progress.service';
import { MemberBudgetService } from 'src/app/services/member-budget/member-budget.service';
import { OnboardingTourService } from './services/onboarding-tour/onboarding-tour.service';
import { HelpIntroService } from './services/help-intro/help-intro.service';
import { CrwdAppUpdateService, isChunkLoadError, reloadOnceForChunkError, registerChunkErrorRecovery } from '@crewdle/ui-kit';
import { OrgUrlSyncService } from 'src/app/services/org-url/org-url-sync.service';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent {

  @ViewChild('supportHost', { read: ViewContainerRef }) private supportHost?: ViewContainerRef;
  private supportPanelLoaded = false;

  version: string;

  protected readonly appVersion = inject(AppVersionService);
  /** Global route progress (guards, resolvers, lazy chunks): the kit's top progress chip renders it. */
  protected readonly navProgress = inject(NavigationProgressService);
  private readonly platform = inject(PlatformService);
  protected readonly redirect = inject(RedirectService);
  /** The concepts tour: "Take the tour" in the user menu. */
  protected readonly tour = inject(OnboardingTourService);
  /** Help after onboarding: the Help button's glow until the person first opens Help. */
  protected readonly helpIntro = inject(HelpIntroService);
  /** The member's monthly budget: user-menu meter + shell banner (admin console → Budgets). */
  protected readonly memberBudget = inject(MemberBudgetService);

  /**
   * Injected purely to construct it: it reports readiness / service-worker state to
   * the native desktop shell so the shell can dismiss its launch splash on real
   * readiness. Nothing reads it back, and it is inert on the web.
   */
  private readonly nativeStatus = inject(NativeStatusService);

  /**
   * Chunk-load recovery is only wired up when there is no Angular service
   * worker. With the worker running, every JS chunk of the running version is
   * already in its cache and version swaps are atomic, so a chunk-load failure
   * is not a stale-index.html symptom any more — and the worker has its own
   * recovery for a genuinely broken cache (UNRECOVERABLE_STATE, handled in
   * AppVersionService). Leaving both on would mean two independent reloaders.
   */
  private readonly chunkRecoveryEnabled = !this.appVersion.isEnabled && !this.platform.is('capacitor');

  /**
   * Whether to mount the kit's `<crwd-app-update>` pill, which is also what
   * starts the kit's /version.json poll (`CrwdAppUpdateService.start()`).
   *
   * Off with the service worker running: it owns "a new version exists" end to
   * end (it compares manifests, downloads into the cache and swaps atomically),
   * and the poller would reload *before* the worker had finished caching the new
   * version — exactly the stale-shell bug the worker exists to prevent.
   *
   * Off under CAPACITOR only (2026-09-29 — was 'shell', which wrongly froze the
   * DESKTOP shell on its first-loaded bundle): mobile bundles the app, so its
   * /version.json is baked and the store/OTA owns updates. The Electron shell
   * loads connect FROM HOSTING — new deploys are real there, and this poll is
   * how a long-running desktop app hears about them.
   */
  protected readonly versionPollEnabled = !this.appVersion.isEnabled && !this.platform.is('capacitor');

  /**
   * The service-worker counterpart of the poll pill: with the worker running
   * (production builds) the app used to swap versions silently on the next
   * navigation, so a parked tab showed nothing — while staging, which has no
   * worker, showed the kit pill (Vincent, 2026-09-19). Mounting this also
   * starts the periodic worker checks; a packaged shell stays excluded for the
   * same reason as the poll.
   */
  protected readonly versionPillEnabled = this.appVersion.isEnabled && !this.platform.is('capacitor');

  /**
   * Whether to offer "Local models" in the user menu. Electron only, and
   * `'electron'` specifically rather than `'shell'`: Capacitor cannot run
   * llama.cpp, so only the desktop shell exposes the `models.*` channels
   * (the same gate `LocalModelsService` applies to itself).
   */

  private readonly modals = inject(ModalService);
  private readonly pushRegistration = inject(PushRegistrationService);
  private readonly nativeNavigation = inject(NativeNavigationService);

  constructor(
    public appHeader: AppHeaderService,
    public appUpdate: CrwdAppUpdateService,
    public nav: SidebarNavService,
    public support: SupportChatbotService,
    private analytics: AnalyticEventService,
    private firebase: FirebaseService,
    private forceUpdate: ForceUpdateService,
    private interval: IntervalService,
    private languageChangeService: LanguageChangeService,
    private localStorage: LocalStorageService,
    private logging: LoggingService,
    private messagingListener: MessagingListenerService,
    private muteService: MuteService,
    private notificationCenter: NotificationCenterService,
    private notification: NotificationService,
    private agentService: AgentService,
    private organization: OrganizationService,
    private productAccess: ProductAccessService,
    private router: Router,
    private localFileBroker: LocalFileBrokerService,
    private localFiles: LocalFilesService,
    private tabNotification: TabNotificationService,
    private theme: ThemeService,
    private translate: TranslateService,
    private user: UserService,
    private whiteLabel: WhiteLabelService,
    protected shellWebUpdate: ShellWebUpdateService,
  ) {
    this.navProgress.start();
    this.logging.log(`AppComponent: v${environment.version}`, LoggingChannels.Default);
    // The rail's rosters and billing listeners; waits on the org internally.
    void this.nav.start();
    // The tab's org lives in its URL (/o/{orgId}): keep the address bar and the org in step.
    inject(OrgUrlSyncService).start();
    // Desktop only (inert elsewhere): answers cloud agents' local-file requests
    // against this machine's shared folders (11-local-files.md).
    void this.localFileBroker.start();
    this.installFolderDropSharing();
    // Create the support panel the first time it is asked for, and never
    // again — keeping it alive preserves the conversation across close/reopen.
    this.support.open$.subscribe((open) => {
      if (open) void this.loadSupportPanel();
    });

    // Recover from post-deploy chunk-load failures that happen outside the
    // router (dynamic imports, deferred views). Service-worker clients don't
    // need this — see chunkRecoveryEnabled.
    if (this.chunkRecoveryEnabled) {
      registerChunkErrorRecovery();
    }
    if (this.versionPillEnabled) {
      this.appVersion.startUpdateChecks();
    }

    this.firebase.firestore.docListen('configs/current', (doc) => {
      if (doc.exists()) {
        const configs: any = doc.data();
        if (this.forceUpdate.requiredUpdate(environment.version, configs.version)) {
          this.logging.log('AppComponent: Need update', LoggingChannels.Default);
          this.router.navigate(['/upgrade']);
        }
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        // Service-worker clients: the new version is already fully cached, so
        // activating + reloading here is local and instant. Doing it on a
        // navigation boundary means the user never straddles two versions.
        if (this.appVersion.ready()) {
          void this.appVersion.activateUpdate();
          return;
        }
        // Everyone else: the kit's /version.json poller noticed a deploy.
        // NOT in the desktop shell (2026-09-29, regression from 6b43d948): there the
        // app is long-lived, so a silent reload on the next click threw away unsent
        // text and in-flight replies after every web deploy. The shell shows the kit's
        // update pill instead and the user reloads when THEY choose.
        if (this.appUpdate.updateAvailable() && !this.platform.is('electron')) {
          location.reload();
          return;
        }
      }
      if (event instanceof NavigationError && this.chunkRecoveryEnabled && isChunkLoadError(event.error)) {
        reloadOnceForChunkError();
      }
    });

    this.interval.startInterval();

    this.version = environment.version;

    const lang = this.localStorage.getItem('lang') || 'en';
    this.languageChangeService.changeLanguage(lang);
    this.translate.setDefaultLang('en');

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Check if isHeaderDisplayed is false and only hide the header if it is
        // We want to let the page controller decide if the header should be displayed or not.
        // A navigation that only changes the query (a page's own ?tab= / ?note= state) keeps
        // the page — and its ngOnInit that showed the header — so it must not hide it
        // (2026-10-23: the rail vanished on Memory and Marketplace tab switches).
        const pathOnly = event.urlAfterRedirects.split(/[?#]/)[0];
        const sameView = pathOnly === this.lastNavigationPath;
        this.lastNavigationPath = pathOnly;
        if (!sameView && !this.isHeaderDisplayed(event)) {
          this.appHeader.isHeaderVisible = false;
        }
        this.analytics.logPage();
      }
    });

    this.languageChangeService.lang = this.localStorage.getItem('lang') || 'en';

    // Open the page a tapped notification or link asks for, with the router (the mobile shell no longer
    // reloads the app for it). Early on purpose: a cold start parks the request until this runs.
    this.nativeNavigation.start();

    this.setupMessagingListener();
    this.setupProductAccessGuard();
    this.setupWhiteLabel();

    // Hold the index.html loader until branding is decided (partner or
    // Crewdle) so the chrome never flips brands in front of the user.
    // settled() is time-capped, so a slow read can't trap anyone here.
    // Mobile app only: ALSO hold it until the first route has rendered — the async guards run
    // first there (auth, App Check, the user doc), and lifting the loader on branding alone
    // uncovered an empty shell for a few seconds. Time-capped (firstRouteRendered). Web and
    // desktop keep the branding-only rule.
    const splashReady = this.platform.is('capacitor')
      ? Promise.all([this.whiteLabel.settled(), firstRouteRendered(this.router)])
      : this.whiteLabel.settled();
    void splashReady.then(() => {
      document.getElementById('splash-screen')?.remove();
    });
    // Mobile app: a downloaded web bundle is offered as "Update ready · Reload" (inert elsewhere).
    this.shellWebUpdate.start();
  }

  /**
   * Feed the current org to the white-label service. Only once an org is
   * present: org data resolves async after auth, and propagating the transient
   * null would clear the cached pre-paint brand mid-boot. Org switches trigger
   * a full reload (see SidebarNavService.switchOrganization), so a one-shot read after
   * isReady() covers the whole session; switching to a partner-less org still
   * clears (org non-null, partnerIdForOrg → null).
   */
  private async setupWhiteLabel() {
    await this.organization.isReady();
    const org = this.organization.data;
    if (org) {
      this.whiteLabel.setPartnerId(partnerIdForOrg(org));
    }
  }

  /**
   * Routes that sit behind the product-access gate. These are the only URLs
   * we force-redirect away from when access is revoked, and the only non-
   * product-disabled targets we auto-redirect back to when access is restored.
   * Room / support / login / register are intentionally reachable without
   * product access (e.g. external meeting invitees).
   */
  private readonly gatedRoutePrefixes = ['/messaging', '/agents', '/tasks'];
  private readonly disabledUrl = '/product-disabled';
  // Home = the default landing (a new chat); productAccessGuard falls back to
  // /agents for orgs without Chat. Single source of truth: defaultProductHome().
  private readonly homeUrl = defaultProductHome();

  private async setupProductAccessGuard() {
    await this.user.isReady();
    await this.organization.isReady();
    if (!this.user.user || !this.organization.data) return;
    // Follows the ACTIVE org: an in-place switch (OrgSwitcherService) or a
    // remote currentOrganizationId change re-evaluates access for the new org
    // before the switcher navigates to the section's home. subscribeTo is
    // idempotent per (org, user).
    this.organization.orgId$.subscribe((orgId) => {
      const uid = this.user.user?.uid;
      if (orgId && uid) {
        this.productAccess.subscribeTo(orgId, uid);
      }
    });

    this.productAccess.allowed$.subscribe((allowed) => {
      if (allowed === null) return;
      const url = this.router.url.split('?')[0].split('#')[0];
      if (!allowed) {
        const isGated = this.gatedRoutePrefixes.some((p) => url === p || url.startsWith(p + '/'));
        if (isGated) {
          this.router.navigate([this.disabledUrl]);
        }
      } else {
        if (url === this.disabledUrl || url.startsWith(this.disabledUrl + '/')) {
          this.router.navigate([this.homeUrl]);
        }
      }
    });
  }

  /** The concepts tour sits over the signed-in shell only, once the org is loaded (review T2). */
  protected get tourCanShow(): boolean {
    return this.appHeader.isHeaderVisible && !!this.organization.data;
  }

  private lastNavigationPath = '';

  public isHeaderDisplayed(event: NavigationEnd) {
    const path = event.url.split('#')[0];

    // Show the sidebar on /messaging, /agents, /tasks, /teams and the hosted /chat
    // surface (not inside a conversation or contact-details sub-view). Chats
    // open in the same pane as agents — one nav; the /chat layout is a thin
    // wrapper with no inner sidebar (it also hides the rail while a code
    // preview takes the full window).
    // Whole first segment only: '/teams' must not match '/teams-no-access' (the
    // SSO failure page has no shell), nor '/chat' a '/chatXYZ' path.
    const first = path.split(/[?/]/)[1] ?? '';
    return ['messaging', 'agents', 'tasks', 'teams', 'chat'].includes(first)
      && ['/conversation', '/contacts'].filter((p) => path.indexOf(p) > -1).length === 0;
  }

  private setupTabTitleUpdater(): void {
    this.messagingListener.totalUnreadCount$.subscribe(count => {
      this.tabNotification.update(count);
    });
  }

  private async setupMessagingListener() {
    await this.user.isReady();
    await this.organization.isReady();
    this.muteService.init();
    this.messagingListener.listen();
    this.notificationCenter.startListening();
    this.memberBudget.start();
    this.setupTabTitleUpdater();
    // Contextual, not at boot (see PushRegistrationService) — no-op on web/Electron.
    void this.pushRegistration.start();
    // Sync contacts with organization (fire-and-forget)
    this.firebase.functions.call('syncOrganizationContacts', {}).catch(() => {});
    // Backfill missing participant docs for current user's conversations (fire-and-forget)
    this.firebase.functions.call('ensureParticipants', {}).catch(() => {});
  }


  /**
   * Lazily creates the support panel. A dynamic import is the only way to keep
   * it (and the kit markdown renderer's highlight.js) out of the initial
   * bundle from an NgModule-based host — `@defer` needs a standalone
   * component's `imports` to defer from.
   */
  private async loadSupportPanel(): Promise<void> {
    if (this.supportPanelLoaded || !this.supportHost) return;
    this.supportPanelLoaded = true;
    const { SupportChatbotComponent } = await import(
      './components/support-chatbot/support-chatbot.component'
    );
    this.supportHost.createComponent(SupportChatbotComponent);
  }

  /**
   * Drag a FOLDER anywhere into the app to share it (11-local-files.md, rethought
   * flow #3). Only directories are intercepted — plain file drops pass through to
   * whatever attachment flow owns them — and the grant still happens in the SHELL'S
   * native confirm, so a page cannot self-grant. Desktop only by construction: the
   * path of a dropped File is only readable through the shell preload.
   */
  private installFolderDropSharing(): void {
    if (!this.localFiles.supported()) return;
    document.addEventListener('dragover', (event) => {
      // Required for drop to fire at all; harmless for the attachment flows,
      // which set their own dropEffect in their handlers.
      event.preventDefault();
    });
    // CAPTURE phase, deliberately: at bubble, the drop reaches the page's
    // attachment handlers FIRST — the agents composer ingested the directory as a
    // bogus 0-byte attachment and the chat bar rejected it as an unsupported file
    // (both measured 2026-09-29) before this handler ever saw it. Directory drops
    // are folder shares, full stop; file drops still fall through to attachments.
    document.addEventListener('drop', (event) => {
      const items = Array.from(event.dataTransfer?.items ?? []);
      const directories = items
        .map((item) => ({ entry: item.webkitGetAsEntry?.(), file: item.getAsFile?.() }))
        .filter((x): x is { entry: FileSystemEntry; file: File } => !!x.entry?.isDirectory && !!x.file);
      if (!directories.length) return; // not ours — file drops belong to attachments
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      void (async () => {
        let shared = false;
        for (const { file } of directories) {
          const path = this.localFiles.droppedPath(file);
          if (!path) continue;
          const before = this.localFiles.roots().length;
          const after = (await this.localFiles.confirmAddPath(path)).length;
          if (after > before) shared = true;
        }
        if (shared) await this.enableLocalFilesForCurrentAgent();
      })();
    }, /* capture */ true);
  }

  /**
   * A folder dropped ON AN AGENT'S CONVERSATION means "let this agent read it" —
   * sharing alone left the agent tool-less and apologising (2026-09-29). The grant
   * is owner-only server-side (assignAgentToSurface), so this can never enable an
   * agent the dropper doesn't own; failure degrades to a pointer at the toggle.
   */
  private async enableLocalFilesForCurrentAgent(): Promise<void> {
    const conversationId = this.router.routerState.snapshot.root.queryParams.conversationId
      ?? new URLSearchParams(window.location.search).get('conversationId');
    if (!conversationId) {
      this.notification.display('local-files.shared-enable-hint', 'success');
      return;
    }
    try {
      await this.agentService.setPlatformTool(conversationId, 'localfiles');
      this.notification.display('local-files.shared-and-enabled', 'success');
    } catch {
      this.notification.display('local-files.shared-enable-hint', 'success');
    }
  }
}
